import { ENTITY_TYPES } from '@sales-center/common'

import type { IEntityFormSetting } from '@user/store/entityFormSettings/typings'

import {
  EntityFormFieldName,
  addLeadCompany,
  addLeadFirstName,
  addLeadLastName,
} from '../constants'
import type { FieldOption } from '../FieldSettingsCard/fieldRenderers/typings'
import { nameRequiredGroup, requiredGroups } from './requiredGroups'

/**
 * Whether the first/last/company "identify the record" group is satisfied by
 * requiring *all* currently-required members (`and`, the historical default)
 * or by requiring *any one* of them (`or`). Stored as the literal string on
 * the `company` row's `options` column — see `getNameRequirementMode`.
 */
export const NAME_REQUIREMENT_AND = 'and'
export const NAME_REQUIREMENT_OR = 'or'

export type NameRequirementMode =
  | typeof NAME_REQUIREMENT_AND
  | typeof NAME_REQUIREMENT_OR

/**
 * Canonical display order for the name group, independent of API ordering —
 * every consumer (radio visibility, both label formatters) sorts into this
 * order rather than trusting the order settings arrive in.
 */
const NAME_FIELD_ORDER: EntityFormFieldName[] = [
  addLeadFirstName,
  addLeadLastName,
  addLeadCompany,
]

const NAME_FIELD_LABELS: Record<string, string> = {
  [addLeadFirstName]: 'First Name',
  [addLeadLastName]: 'Last Name',
  [addLeadCompany]: 'Company Name',
}

/**
 * The name-group members currently marked `required` for the given entity,
 * sorted into `NAME_FIELD_ORDER`.
 *
 * This is the single place the "is the radio group visible" check
 * (`length >= 2`) and both label formatters below read from, so the ordering
 * rule lives in exactly one place.
 */
export function getRequiredNameFields(
  settings: IEntityFormSetting[],
  entityType: ENTITY_TYPES,
): EntityFormFieldName[] {
  const members = requiredGroups[nameRequiredGroup]
  const required = new Set(
    settings
      .filter(
        (setting) =>
          setting.entityType === entityType &&
          members.includes(setting.fieldId) &&
          setting.required,
      )
      .map((setting) => setting.fieldId),
  )
  return NAME_FIELD_ORDER.filter((fieldId) => required.has(fieldId))
}

/**
 * The stored mode for the given entity, read off the `company` row. `||`
 * (not `??`) is deliberate — it coerces `''` as well as `null`/`undefined` to
 * the default, matching the existing `settingItem.options || <default>`
 * idiom (`OpportunityStageField.tsx:45`).
 */
export function getNameRequirementMode(
  settings: IEntityFormSetting[],
  entityType: ENTITY_TYPES,
): NameRequirementMode {
  const companyRow = settings.find(
    (setting) =>
      setting.entityType === entityType && setting.fieldId === addLeadCompany,
  )
  return (companyRow?.options as NameRequirementMode) || NAME_REQUIREMENT_AND
}

/** `'First Name'`, `'First Name and Last Name'`, `'First Name, Last Name, and Company Name'` */
const joinWithConjunction = (labels: string[], conj: 'and' | 'or') =>
  labels.length <= 2
    ? labels.join(` ${conj} `)
    : `${labels.slice(0, -1).join(', ')}, ${conj} ${labels[labels.length - 1]}`

/**
 * The radio group's two label/value options for the currently-required name
 * fields. Oxford comma at 3 members — this is a different formatter from
 * `buildNameRequirementErrorMessage` below, by design (see there).
 */
export function buildNameRequirementOptions(
  requiredFields: EntityFormFieldName[],
): FieldOption[] {
  const labels = requiredFields.map((fieldId) => NAME_FIELD_LABELS[fieldId])
  return [
    {
      label: `Require ${joinWithConjunction(labels, 'and')}`,
      value: NAME_REQUIREMENT_AND,
    },
    {
      label: `Require ${joinWithConjunction(labels, 'or')}`,
      value: NAME_REQUIREMENT_OR,
    },
  ]
}

/**
 * The section-level validation message for "or" mode. Deliberately a
 * *different* formatter from `buildNameRequirementOptions` — it repeats `or`
 * between every member with no commas at any count (`First Name or Last Name
 * or Company Name is required` at 3 members), per the confirmed AC copy. Do
 * not "normalise" this into the label's Oxford-comma form.
 */
export function buildNameRequirementErrorMessage(
  requiredFields: EntityFormFieldName[],
): string {
  const labels = requiredFields.map((fieldId) => NAME_FIELD_LABELS[fieldId])
  return `${labels.join(' or ')} is required`
}
